import { MoreHorizontal } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { parseError } from '@/lib/errors'
import { PipelineSection, useUpdateLeadPipelineFields } from '@/models/myPipeline'

interface PipelineRowMenuProps {
  leadId: string
  legalName: string
  /** The table the row is in. */
  shownAs: PipelineSection.DEAL | PipelineSection.WARM
  pipelineSection: PipelineSection
  /** The viewed month, "yyyy-MM-01"; a warm account moved to deals is expected to close in it. */
  month?: string
}

/** Move a row between deals and warm accounts, take it off the page, or hand it back to the status rules. */
export function PipelineRowMenu({
  leadId,
  legalName,
  shownAs,
  pipelineSection,
  month,
}: PipelineRowMenuProps) {
  const update = useUpdateLeadPipelineFields()
  const place = (next: PipelineSection) =>
    update.mutate(
      next === PipelineSection.DEAL && month
        ? { id: leadId, pipelineSection: next, expectedCloseMonth: month }
        : { id: leadId, pipelineSection: next },
      { onError: (error) => toast.error(parseError(error)) }
    )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Pipeline options for ${legalName}`}
          disabled={update.isPending}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {shownAs === PipelineSection.WARM ? (
          <DropdownMenuItem onClick={() => place(PipelineSection.DEAL)}>Move to deals</DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => place(PipelineSection.WARM)}>
            Move to warm accounts
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => place(PipelineSection.HIDDEN)}>
          Remove from pipeline
        </DropdownMenuItem>
        {pipelineSection !== PipelineSection.AUTO && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => place(PipelineSection.AUTO)}>Reset to auto</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
